import store from '@/store/store'
import { logger } from '@logger'
import { sendClientSeed } from '@core/graphQL/services/crashService'
import { CLIENT_SEED_PATTERN, isValidClientSeed } from './crashFairness'

const MODULE = 'Client Seed'

/**
 * The player's contribution to the round's provably-fair outcome.
 *
 * Provably fair v2 folds three player client seeds into the round seed alongside
 * the server seed, so a round's crash point is not something the operator chose
 * alone. This module owns the client half: the player's seed, where it is kept,
 * and handing it to the backend when they bet.
 *
 * ── the seed belongs to the player ────────────────────────────────────────────
 * It is chosen ONCE and reused for every round that player bets in, until they
 * change it — which is what makes it theirs. A freshly minted seed per round would
 * be just as random, but the player could never point at a value and say "that one
 * is mine, and here it is in the round record". Being able to set it, write it
 * down, and then find it in a published round is the whole point.
 *
 * Reusing one seed across rounds costs nothing cryptographically: the server seed
 * is fresh per round and committed before the client seed is ever sent, so knowing
 * a player's seed in advance gives the operator no freedom. (Its fallbacks are
 * derived from that committed seed for the same reason — see
 * deriveFallbackClientSeed.)
 *
 * ── the one hard rule ─────────────────────────────────────────────────────────
 * None of this may affect a bet. Submission is fire-and-forget: no await in the
 * bet path, no throw, no toast, no state the betting flow reads. If the request
 * fails, is rejected, or never leaves the device, the round seals with a server
 * fallback in that slot — a normal, fully verifiable outcome.
 */

// 32 CSPRNG bytes as 64 lowercase hex characters — the same shape the backend's
// derived fallbacks have, so a player seed and a fallback are indistinguishable in
// form and neither stands out in the published record.
const SEED_BYTES = 32

// Per player: two people sharing a browser, or one launch token replaced by
// another, must not silently inherit each other's seed.
const storageKey = (playerId) => `crash.pf.clientSeed.${playerId}`

// localStorage is not always there — operator iframes partition it, private modes
// disable it, and some webviews throw on access rather than returning null. The
// seed still has to work in all of those, so a per-session memory copy backs it.
const memory = new Map()

const readStored = (playerId) => {
  try {
    return globalThis.localStorage?.getItem(storageKey(playerId)) ?? null
  } catch {
    return null
  }
}

const writeStored = (playerId, seed) => {
  try {
    globalThis.localStorage?.setItem(storageKey(playerId), seed)
    return true
  } catch {
    // Not fatal: `memory` keeps the seed for this session, so the player still gets
    // a stable seed — it just does not survive a reload.
    return false
  }
}

/**
 * The first crypto object that can actually produce random bytes.
 *
 * Not just `globalThis.crypto`: some environments expose one that lacks
 * getRandomValues while a working implementation sits on another global (older
 * webviews, `msCrypto` on legacy Edge). Taking the first object we find and giving
 * up if it is incomplete would silently disable the feature for those players —
 * they would contribute no seed and never be told why.
 */
const resolveCrypto = () => {
  const candidates = [globalThis.crypto, globalThis.msCrypto]
  return candidates.find((c) => typeof c?.getRandomValues === 'function') ?? null
}

/**
 * Mint a client seed.
 *
 * A CSPRNG is required rather than preferred: Math.random is not one, and a
 * predictable client seed silently weakens the very thing this feature exists to
 * strengthen. Returns null if none is available, and the caller then sends nothing
 * — a server fallback is strictly better than contributing guessable entropy while
 * claiming to contribute entropy.
 */
export const generateClientSeed = () => {
  const cryptoObj = resolveCrypto()

  if (!cryptoObj) {
    logger.warn(MODULE, 'No CSPRNG available — cannot mint a client seed')
    return null
  }

  const bytes = new Uint8Array(SEED_BYTES)
  cryptoObj.getRandomValues(bytes)

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

const currentPlayerId = () => store.getState().session?.details?.playerId ?? null

/**
 * The player's client seed, minting and persisting one on first use.
 *
 * @param {string} [playerId] - defaults to the signed-in player
 * @returns {string|null} null only when there is no player, or no CSPRNG
 */
export const getClientSeed = (playerId = currentPlayerId()) => {
  if (!playerId) return null

  const cached = memory.get(playerId)
  if (cached) return cached

  const stored = readStored(playerId)

  // A stored value is re-validated rather than trusted. It may predate a change to
  // the permitted alphabet, or have been edited by hand in devtools; either way a
  // seed the backend would reject is worse than a fresh one, because it silently
  // costs the player their slot every round.
  if (stored && isValidClientSeed(stored)) {
    memory.set(playerId, stored)
    return stored
  }

  const minted = generateClientSeed()
  if (!minted) return null

  memory.set(playerId, minted)
  writeStored(playerId, minted)

  return minted
}

/**
 * Replace the player's client seed.
 *
 * @param {string} seed
 * @param {string} [playerId]
 * @returns {{ ok: boolean, seed: string|null, error: string|null, persisted: boolean }}
 */
export const setClientSeed = (seed, playerId = currentPlayerId()) => {
  if (!playerId) {
    return { ok: false, seed: null, error: 'Not signed in.', persisted: false }
  }

  const trimmed = typeof seed === 'string' ? seed.trim() : ''

  // Trimming only the OUTSIDE of what was typed: a leading space is a paste
  // artefact, whereas the seed itself cannot contain whitespace at all (the
  // alphabet forbids it), so this cannot silently alter a legitimate value.
  if (!isValidClientSeed(trimmed)) {
    return {
      ok: false,
      seed: null,
      error: 'Use 1–64 characters: letters, numbers, hyphen or underscore.',
      persisted: false,
    }
  }

  memory.set(playerId, trimmed)
  const persisted = writeStored(playerId, trimmed)

  // Taking effect NEXT round, not this one, is not a limitation to paper over: a
  // seed already submitted for the round in play is sealed into it, and letting a
  // player swap it after seeing the round open would be exactly the influence the
  // scheme is built to prevent.
  logger.info(MODULE, 'Client seed changed', { playerId, length: trimmed.length, persisted })

  return { ok: true, seed: trimmed, error: null, persisted }
}

/**
 * Mint and store a fresh seed for the player.
 *
 * @param {string} [playerId]
 * @returns {{ ok: boolean, seed: string|null, error: string|null, persisted: boolean }}
 */
export const randomiseClientSeed = (playerId = currentPlayerId()) => {
  const minted = generateClientSeed()

  if (!minted) {
    return { ok: false, seed: null, error: 'Secure random numbers are unavailable here.', persisted: false }
  }

  return setClientSeed(minted, playerId)
}

// Rounds this player has already sent a seed for. Bounded because a long session
// plays hundreds of rounds and the only thing it must remember is the handful
// still in play.
const MAX_TRACKED_ROUNDS = 20
let requestedRounds = []

/**
 * Send the player's client seed for the round currently in play, at most once per
 * round.
 *
 * Safe to call from anywhere, as often as you like: it resolves the round from the
 * store, no-ops when there is no round or one was already requested, and never
 * blocks or throws. Called twice per bet by design — once when the player presses
 * Bet, once when the deferred PLACE_BET actually goes out — because the press can
 * land while the previous round is still running, and the emit is guaranteed to
 * land inside the betting window. The per-round guard collapses that to a single
 * request in the normal case.
 *
 * @returns {void} deliberately not a promise, so no caller can await it
 */
export const submitClientSeedForCurrentRound = () => {
  const roundId = store.getState().events?.round?.roundId ?? null

  if (!roundId) return
  if (requestedRounds.includes(roundId)) return

  const playerId = currentPlayerId()
  const clientSeed = getClientSeed(playerId)
  if (!clientSeed) return

  // Belt and braces: getClientSeed only ever returns a validated seed, but this
  // value travels into a hash input whose field separator the alphabet excludes,
  // so the check is worth having at the point of use too.
  if (!isValidClientSeed(clientSeed)) {
    logger.error(MODULE, 'Stored seed is not in the permitted alphabet — not sending', {
      pattern: String(CLIENT_SEED_PATTERN),
    })
    return
  }

  requestedRounds.push(roundId)
  if (requestedRounds.length > MAX_TRACKED_ROUNDS) {
    requestedRounds = requestedRounds.slice(-MAX_TRACKED_ROUNDS)
  }

  // roundId is sent for CORRELATION only. The backend resolves the round itself and
  // rejects a mismatch, which is what turns "my client was a round behind" into a
  // clean no-op instead of a seed landing on a round the player never saw the
  // commitment for.
  // Wrapped, not just `.then().catch()`-ed. The rule this module promises is that
  // it can never throw into the bet path, and that only holds while the service
  // returns a promise — a refactor that returned undefined would throw a TypeError
  // synchronously, right where a bet is being placed. Cheap to make unconditional.
  try {
    const pending = sendClientSeed({ clientSeed, roundId })

    if (typeof pending?.then === 'function') {
      pending
        .then((result) => {
          logger.info(MODULE, result?.accepted ? 'Client seed accepted' : 'Client seed not used', {
            roundId,
            slot: result?.slot ?? null,
            reason: result?.reason ?? null,
            seedCount: result?.seedCount ?? null,
          })
        })
        .catch((error) => {
          // Nothing to do and nothing to tell the player: the round seals with a
          // server fallback in this slot and stays fully verifiable.
          logger.warn(MODULE, 'Client seed request failed — round will use a server fallback', {
            roundId,
            error: error?.message,
          })
        })
    }
  } catch (error) {
    // Same reasoning: never propagate.
    logger.warn(MODULE, 'Client seed request threw — round will use a server fallback', {
      roundId,
      error: error?.message,
    })
  }
}

/** Test seam: forget which rounds have been requested and any cached seed. */
export const resetClientSeedTracking = () => {
  requestedRounds = []
  memory.clear()
}
